/**
 * My Calendar — the recruiter's personal calendar.
 *
 * One view over three sources (GET /api/crm/calendar): booked callbacks (cyan),
 * follow-up tasks with a due date (amber), and the recruiter's own personal
 * events (user-chosen color). Callbacks/tasks are read-only here — clicking one
 * jumps to its contact; only personal events are created/edited inline.
 *
 * "Calendars" you can show are of two kinds, both filterable:
 *   • Event types — toggle Callbacks / Tasks / Personal events on/off (everyone).
 *   • Recruiters — everyone can overlay several teammates' callbacks & tasks at
 *     once (or "All recruiters"), owner decision 2026-09-03. When more than one
 *     person is shown, items are color-coded per person (chip fill still encodes
 *     the event type). Personal events always stay private to their owner, so
 *     they appear only when your own calendar is part of the selection. Any
 *     callback shown on a teammate's calendar can be "taken" (self-assigned) —
 *     see the Take button on agenda rows / the day-detail popover.
 *
 * Month grid + a one-day agenda (owner ask 2026-10-09): the arrows (and the
 * left and right arrow keys) step a day, so earlier days are one press away.
 * Every callback whose time has passed shows what happened on the call (the
 * outcome pill, components/calendar/CallbackOutcome.tsx), worked out by the
 * server from the records and the AI, and correctable from the row. Times
 * render in the viewer's local zone.
 * Refetches on a 60s poll and on the cross-surface crm:stats-changed event, the
 * same rhythm as the sidebar badges, so a callback booked elsewhere shows up.
 *
 * OUT OF OFFICE is the one layer here that is NOT personal. Absences come from
 * crm.user_absences (GET /api/crm/absences) — a separate, team-visible record,
 * not a calendar event — so they are shown for the WHOLE team regardless of the
 * recruiter selection, and they are drawn deliberately unlike events (a dashed
 * neutral band with a person icon, never a colored chip) so nobody reads
 * "Dana — out" as one of Dana's private events. The privacy rule for personal
 * events is untouched: those still appear only when your own calendar is in the
 * selection. The absence fetch rides the same 60s poll and fails soft — if it
 * errors the calendar renders exactly as before and the strip says so.
 *
 * TEAM DAY (recruiters' ask 2026-10-16; components/calendar/TeamDayView.tsx):
 * a third view, one day with one column per selected recruiter on a shared
 * time axis. The same arrows, Today and arrow keys step the day (the day is
 * shared with Agenda); the recruiter picker, and a row of name chips above the
 * grid, choose the columns. It alone asks GET /calendar for include_busy, so
 * a teammate's blocking events show as anonymous "Busy" blocks; Month and
 * Agenda read exactly what they always did.
 *
 * PHONES (useIsPhone, below `sm`) get the same controls in a layout a thumb can
 * use: the month arrows and Today share the title row, and whose-calendars,
 * Month | Agenda, refresh and a "+" New event make one toolbar row under it;
 * the event-type toggles are one swipeable strip (the two explanatory notes
 * stay desktop-only); a month cell shows the day's items as dots and a "+N"
 * count instead of unreadable one-letter chips, and a TAP opens the day's
 * sheet — its list, "New event this day" and "Mark someone out this day" —
 * where a desktop click still creates an event at 9am. Every control is
 * mounted exactly once per tier; desktop renders as before.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  CalendarRange,
  Check,
  ChevronLeft,
  Columns,
  ChevronRight,
  Loader2,
  PhoneIncoming,
  Plus,
  UserMinus,
  X,
} from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { CalendarItem, CalendarSourceKind, UserAbsence } from '../types';
import { clockTime, dayKey, dayLabel, getPref, notifyStatsChanged, setPref, STATS_CHANGED_EVENT, useIsPhone, useTeam } from '../lib';
import { PageHeader, RefreshButton, SegmentedControl } from '../components';
import EventModal from '../components/calendar/EventModal';
import AbsenceModal, { absenceKindLabel, backOnLabel, dayKeyToDate, shortDay } from '../components/calendar/AbsenceModal';
import WhosOutBar from '../components/calendar/WhosOutBar';
import RecruiterMultiSelect, { AssigneeSelection, toggleAssignee } from '../components/calendar/RecruiterMultiSelect';
import TeamDayView from '../components/calendar/TeamDayView';
import { teamColumns } from '../components/calendar/teamDayLogic';
import { DEFAULT_EVENT_COLOR, eventColor, personColor } from '../components/calendar/colors';
import { expandOccurrences } from '../components/calendar/recurrence';
import {
  OutcomeBlock,
  OutcomePill,
  OutcomeSelect,
  isPastCallback,
  outcomeDotCls,
  outcomeTally,
  toneDot,
} from '../components/calendar/CallbackOutcome';
import { Alert, Z } from '../components/ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

const pad = (n: number) => String(n).padStart(2, '0');
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfWeekSun = (d: Date) => addDays(startOfDay(d), -startOfDay(d).getDay());
const localDayKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const firstName = (n: string | null | undefined) => (n || '').trim().split(/\s+/)[0] || n || '';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const chipBase = 'inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium';

// Unique per rendered instance — recurring occurrences share a source_id, so the
// per-occurrence instance_key keeps React keys distinct.
const keyOf = (it: CalendarItem) => `${it.source_kind}-${it.source_id}-${it.instance_key ?? ''}`;

// The three "event-type calendars" the legend toggles.
const ALL_KINDS: CalendarSourceKind[] = ['callback', 'task', 'event'];
const KIND_META: Record<CalendarSourceKind, { label: string; dot: string }> = {
  callback: { label: 'Callbacks', dot: 'bg-cyan-500' },
  task: { label: 'Tasks', dot: 'bg-amber-500' },
  event: { label: 'Personal events', dot: 'bg-slate-500' },
};

function loadKinds(): Set<CalendarSourceKind> {
  const parts = getPref('calendarKinds', ALL_KINDS.join(','))
    .split(',')
    .map((s) => s.trim())
    .filter((k): k is CalendarSourceKind => (ALL_KINDS as string[]).includes(k));
  return new Set(parts.length ? parts : ALL_KINDS);
}

function loadAssignees(meId: string | undefined): AssigneeSelection {
  const raw = getPref('calendarAssignees', '');
  if (raw === 'all') return { all: true, ids: [] };
  const ids = raw.split(',').map((s) => s.trim()).filter(Boolean);
  if (ids.length) return { all: false, ids };
  return { all: false, ids: meId ? [meId] : [] };
}

function isTerminal(item: CalendarItem): boolean {
  if (item.source_kind === 'callback') return ['completed', 'cancelled', 'no_show'].includes(item.status || '');
  if (item.source_kind === 'task') return ['done', 'cancelled'].includes(item.status || '');
  return false;
}

// Drawn struck through / faded: a finished item, except a callback that carries
// its outcome (the outcome pill says how it ended, which a strike-through hid).
function struck(item: CalendarItem): boolean {
  return isTerminal(item) && !(item.source_kind === 'callback' && item.outcome);
}

// A callback sitting on someone else's calendar can be "taken" (self-assigned)
// by any teammate — tasks/events keep their existing reassignment paths (the
// task card, the contact's own worklist) and aren't offered here.
function canTake(item: CalendarItem, meId: string | undefined): boolean {
  return item.source_kind === 'callback' && !!item.owner_user_id && item.owner_user_id !== meId && !isTerminal(item);
}

type CalendarView = 'month' | 'agenda' | 'team';
const readView = (): CalendarView => {
  const v = getPref('calendarView', 'month');
  return v === 'agenda' || v === 'team' ? v : 'month';
};

function chipCls(item: CalendarItem): string {
  if (item.source_kind === 'callback') return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300';
  if (item.source_kind === 'task') return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300';
  return eventColor(item.color).chip;
}

function dotCls(item: CalendarItem): string {
  if (item.source_kind === 'callback') return 'bg-cyan-500';
  if (item.source_kind === 'task') return 'bg-amber-500';
  return eventColor(item.color).dot;
}

/** The out-of-office layer is toggled on its own, not through visibleKinds —
 *  it is not one of the three CalendarSourceKind "event-type calendars". */
const ABSENCE_PREF = 'calendarAbsences';

type AbsenceModalState =
  | { mode: 'create'; day: string | null }
  | { mode: 'view'; absence: UserAbsence };

type ModalState =
  | { mode: 'create'; start: Date; end: Date; allDay: boolean; color: string }
  | {
      mode: 'edit';
      eventId: string;
      title: string;
      start: Date;
      end: Date;
      allDay: boolean;
      location: string | null;
      description: string | null;
      color: string | null;
      recurrence: 'weekly' | null;
      recurrenceDays: number[] | null;
      recurrenceUntil: string | null;
      blocksBooking: boolean;
    };

export default function Calendar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { team, byId } = useTeam();
  // The ONE breakpoint read: picks the phone header arrangement, the dot-and-
  // count month cells and the tap-opens-the-day behaviour (header comment).
  const isPhone = useIsPhone();

  const [view, setView] = useState<CalendarView>(readView);
  const [anchor, setAnchor] = useState<Date>(() => new Date());
  // The Agenda and Team day show ONE day; the arrows step a day back or forward.
  const [agendaDay, setAgendaDay] = useState<Date>(() => startOfDay(new Date()));
  const [assignees, setAssignees] = useState<AssigneeSelection>(() => loadAssignees(user?.id));
  const [visibleKinds, setVisibleKinds] = useState<Set<CalendarSourceKind>>(() => loadKinds());
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modal, setModal] = useState<ModalState | null>(null);
  const [dayDetail, setDayDetail] = useState<Date | null>(null);
  const [takingId, setTakingId] = useState<string | null>(null);
  const [takeError, setTakeError] = useState<string | null>(null);

  // ---- Out of office (team-visible; see the header comment) -----------------
  const [absences, setAbsences] = useState<UserAbsence[]>([]);
  const [absenceError, setAbsenceError] = useState<string | null>(null);
  const [absenceLoading, setAbsenceLoading] = useState(true);
  const [showAbsences, setShowAbsences] = useState<boolean>(() => getPref(ABSENCE_PREF, '1') !== '0');
  const [absenceModal, setAbsenceModal] = useState<AbsenceModalState | null>(null);

  // Everyone can overlay teammates' calendars (owner decision 2026-09-03).
  const effAssignees: AssigneeSelection = assignees;

  // More than one recruiter on screen → color-code by person.
  const multiPerson = effAssignees.all || effAssignees.ids.length > 1;
  // Showing anyone other than just yourself (their personal events stay private).
  const includesOthers = effAssignees.all || effAssignees.ids.some((id) => id !== user?.id);
  // You can add events whenever your own calendar is part of the view (so the
  // new event actually shows up).
  const canCreate = effAssignees.all || (!!user?.id && effAssignees.ids.includes(user.id));

  // Wire the selection to the query: omit the param for the plain "just me" case
  // so the default view takes the fast (implicit-self) path.
  const assigneeParam = useMemo(() => {
    if (assignees.all) return 'all';
    const ids = assignees.ids;
    if (ids.length === 0) return null;
    if (ids.length === 1 && ids[0] === user?.id) return null;
    return ids.join(',');
  }, [assignees, user?.id]);

  // If auth resolved after first render (no stored pref, id was unknown), seed
  // the selection to "just me" so the default view and event creation work.
  useEffect(() => {
    if (user?.id && !assignees.all && assignees.ids.length === 0) {
      setAssignees(loadAssignees(user.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const setViewPref = (v: CalendarView) => {
    setView(v);
    setPref('calendarView', v);
  };

  const updateAssignees = (v: AssigneeSelection) => {
    setAssignees(v);
    setPref('calendarAssignees', v.all ? 'all' : v.ids.join(','));
  };

  const toggleKind = (k: CalendarSourceKind) => {
    const next = new Set(visibleKinds);
    if (next.has(k)) next.delete(k);
    else next.add(k);
    setVisibleKinds(next);
    setPref('calendarKinds', [...next].join(','));
  };

  const range = useMemo(() => {
    if (view !== 'month') {
      const from = startOfDay(agendaDay);
      return { from, to: addDays(from, 1) };
    }
    const gridStart = startOfWeekSun(startOfMonth(anchor));
    return { from: gridStart, to: addDays(gridStart, 42) };
  }, [view, anchor, agendaDay]);

  // Agenda and Team day: the left and right arrow keys step a day, unless a
  // field, a dialog or a select has the keyboard.
  useEffect(() => {
    if (view === 'month') return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const t = e.target as HTMLElement | null;
      if (t && (t.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [aria-modal="true"]'))) return;
      if (document.querySelector('[aria-modal="true"]')) return;
      setAgendaDay((d) => addDays(d, e.key === 'ArrowLeft' ? -1 : 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [view]);

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      if (!silent) setAbsenceLoading(true);
      const params: Record<string, string> = { from: range.from.toISOString(), to: range.to.toISOString() };
      if (assigneeParam) params.assignees = assigneeParam;
      // Team day only: teammates' blocking events as anonymous "Busy" time.
      if (view === 'team') params.include_busy = 'true';
      // Absences are calendar DAYS, not instants — the API takes YYYY-MM-DD.
      // The window is widened to always cover today + the next week, so paging
      // to another month can never make the "who's out today" strip claim
      // everyone is in (an empty answer there would read as "all clear").
      const now = new Date();
      const todayStr = localDayKey(now);
      const weekOutStr = localDayKey(addDays(now, 8));
      const fromStr = localDayKey(range.from);
      const toStr = localDayKey(range.to);
      // Because the window is stretched to always include today AND the visible
      // month, its SPAN grows without bound as you page away from today — and
      // GET /absences rejects anything wider than 366 days. Paging back or
      // forward about a year would 400 and silently empty the strip, which
      // reads as "everyone is in". Clamp to the server's limit; a year of
      // absences is far more than either surface displays.
      const absFrom = fromStr < todayStr ? fromStr : todayStr;
      const absToWanted = toStr > weekOutStr ? toStr : weekOutStr;
      const absMax = localDayKey(addDays(new Date(`${absFrom}T00:00:00`), 365));
      const absParams = {
        from: absFrom,
        to: absToWanted > absMax ? absMax : absToWanted,
      };

      // Two independent fetches on one rhythm: a failing absence board must
      // never take the calendar down with it (and vice versa).
      const [cal, abs] = await Promise.allSettled([
        api.get('/api/crm/calendar', { params }),
        api.get('/api/crm/absences', { params: absParams }),
      ]);

      if (cal.status === 'fulfilled') {
        setItems((cal.value.data?.events || []) as CalendarItem[]);
        setError(null);
      } else {
        setError(errMsg(cal.reason));
      }
      if (abs.status === 'fulfilled') {
        setAbsences((abs.value.data?.absences || []) as UserAbsence[]);
        setAbsenceError(null);
      } else {
        setAbsenceError(errMsg(abs.reason));
      }
      setLoading(false);
      setAbsenceLoading(false);
    },
    [range, assigneeParam, view]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const silent = () => load(true);
    const timer = window.setInterval(silent, 60_000);
    window.addEventListener(STATS_CHANGED_EVENT, silent);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener(STATS_CHANGED_EVENT, silent);
    };
  }, [load]);

  // Event-type filter (the per-type "calendars") applied before grouping.
  // Busy time (Team day only) is availability, not one of the toggles.
  const filteredItems = useMemo(
    () => items.filter((it) => it.source_kind === 'busy' || visibleKinds.has(it.source_kind)),
    [items, visibleKinds]
  );

  // The recurring base rows keep the true series start/end + rule, so editing
  // any occurrence edits the whole series. Looked up by source_id on click.
  const baseEventsById = useMemo(() => {
    const m = new Map<string, CalendarItem>();
    for (const it of filteredItems) if (it.source_kind === 'event') m.set(it.source_id, it);
    return m;
  }, [filteredItems]);

  // Expand recurring events into per-occurrence items within the visible window.
  const expandedItems = useMemo(() => {
    const out: CalendarItem[] = [];
    for (const it of filteredItems) {
      if ((it.source_kind === 'event' || it.source_kind === 'busy') && it.recurrence === 'weekly') {
        out.push(...expandOccurrences(it, range.from, range.to));
      } else {
        out.push(it);
      }
    }
    return out;
  }, [filteredItems, range]);

  // ---- Per-person helpers (only meaningful in multiPerson mode) -------------
  const ownerLabel = useCallback(
    (id: string | null | undefined): string => {
      if (!id) return 'Unassigned';
      if (id === user?.id) return 'You';
      return byId.get(id)?.name || 'Teammate';
    },
    [byId, user?.id]
  );
  const leadingDot = (it: CalendarItem) => (multiPerson ? personColor(it.owner_user_id).dot : dotCls(it));

  // Distinct recruiters currently on screen, for the "Calendars:" legend.
  const shownOwners = useMemo(() => {
    if (!multiPerson) return [] as { id: string; label: string }[];
    const seen = new Map<string, string>();
    for (const it of filteredItems) {
      const key = it.owner_user_id || '';
      if (!seen.has(key)) seen.set(key, ownerLabel(it.owner_user_id));
    }
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [multiPerson, filteredItems, ownerLabel]);

  // Group by local day; all-day events sort before timed within a day. A
  // multi-day event (e.g. a 3-day all-day OOO hold, or a timed block crossing
  // midnight) is fanned onto EVERY local day it covers, not just its start —
  // clamped to the visible range so a long span can't spin the loop.
  const itemsByDay = useMemo(() => {
    const map = new Map<string, CalendarItem[]>();
    const push = (k: string, it: CalendarItem) => {
      const arr = map.get(k);
      if (arr) arr.push(it);
      else map.set(k, [it]);
    };
    const rangeStart = startOfDay(range.from);
    const rangeEnd = startOfDay(range.to);
    for (const it of expandedItems) {
      const first = startOfDay(new Date(it.start));
      const last = startOfDay(new Date(it.end));
      let d = first < rangeStart ? rangeStart : first;
      const end = last > rangeEnd ? rangeEnd : last;
      if (d > end) {
        push(dayKey(it.start), it); // wholly outside the window (shouldn't happen) — keep on start day
        continue;
      }
      for (; d <= end; d = addDays(d, 1)) push(localDayKey(d), it);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.all_day === b.all_day ? a.start.localeCompare(b.start) : a.all_day ? -1 : 1));
    }
    return map;
  }, [expandedItems, range]);

  // Absences fanned onto every local day they cover, clamped to the visible
  // window. `starts_on`/`ends_on` are YYYY-MM-DD, which compares correctly as a
  // plain string — no instant math, so there is no boundary hour to get wrong.
  const absencesByDay = useMemo(() => {
    const map = new Map<string, UserAbsence[]>();
    if (!showAbsences) return map;
    const fromKey = localDayKey(range.from);
    const toKey = localDayKey(range.to);
    for (const a of absences) {
      if (!a.starts_on || !a.ends_on) continue;
      const firstKey = a.starts_on < fromKey ? fromKey : a.starts_on;
      const lastKey = a.ends_on > toKey ? toKey : a.ends_on;
      if (firstKey > lastKey) continue;
      for (let d = dayKeyToDate(firstKey); localDayKey(d) <= lastKey; d = addDays(d, 1)) {
        const k = localDayKey(d);
        const arr = map.get(k);
        if (arr) arr.push(a);
        else map.set(k, [a]);
      }
    }
    for (const arr of map.values()) arr.sort((a, b) => (a.user_name || '').localeCompare(b.user_name || ''));
    return map;
  }, [absences, showAbsences, range]);

  const absenceName = useCallback(
    (a: UserAbsence) => (a.user_id === user?.id ? 'You' : firstName(a.user_name) || 'Teammate'),
    [user?.id]
  );

  const absenceTitle = (a: UserAbsence) =>
    `${a.user_name || 'Recruiter'} · ${absenceKindLabel(a.kind)} · ${
      a.starts_on === a.ends_on ? shortDay(a.starts_on) : `${shortDay(a.starts_on)} – ${shortDay(a.ends_on)}`
    }${a.note ? ` · ${a.note}` : ''}`;

  /** A day band on the month grid — dashed + neutral so it never reads as an event. */
  const absenceBand = (a: UserAbsence) => (
    <button
      key={`abs-${a.id}`}
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        setAbsenceModal({ mode: 'view', absence: a });
      }}
      title={absenceTitle(a)}
      className="flex w-full items-center gap-1 rounded border border-dashed border-gray-400 bg-gray-100 px-1.5 py-0.5 text-left text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-500 dark:bg-gray-700/60 dark:text-gray-300 dark:hover:bg-gray-700"
    >
      <UserMinus className="h-3 w-3 flex-shrink-0" />
      <span className="truncate">{absenceName(a)} — out</span>
    </button>
  );

  // Self-assign a teammate's callback straight from the calendar. The PATCH
  // itself carries no permission check beyond CRM tenant membership (any
  // recruiter may already reassign any callback — see routes/crm.js), so this
  // is purely a convenience action; the response drives the timeline copy
  // ("X took this callback").
  const takeCallback = async (item: CalendarItem) => {
    if (!user?.id || takingId) return;
    setTakingId(item.source_id);
    setTakeError(null);
    try {
      await api.patch(`/api/crm/callbacks/${item.source_id}`, { assignee_user_id: user.id });
      notifyStatsChanged();
      await load(true);
    } catch (e) {
      setTakeError(errMsg(e));
    } finally {
      setTakingId(null);
    }
  };

  const handleItemClick = (item: CalendarItem) => {
    if (item.source_kind === 'event' && item.editable) {
      // Edit the SERIES (the base row), not the clicked occurrence, so the true
      // start date + recurrence rule are preserved.
      const base = baseEventsById.get(item.source_id) || item;
      setModal({
        mode: 'edit',
        eventId: base.source_id,
        title: base.title,
        start: new Date(base.start),
        end: new Date(base.end),
        allDay: base.all_day,
        location: base.location ?? null,
        description: base.description ?? null,
        color: base.color ?? null,
        recurrence: base.recurrence ?? null,
        recurrenceDays: base.recurrence_days ?? null,
        recurrenceUntil: base.recurrence_until ?? null,
        blocksBooking: base.blocks_booking ?? true,
      });
    } else if (item.contact_id) {
      navigate(`/crm/contacts/${item.contact_id}`);
    }
  };

  const openCreate = (day?: Date) => {
    if (!canCreate) return;
    let base: Date;
    if (day) {
      base = new Date(day.getFullYear(), day.getMonth(), day.getDate(), 9, 0, 0, 0);
    } else {
      base = new Date();
      base.setMinutes(0, 0, 0);
      base.setHours(base.getHours() + 1);
    }
    setModal({ mode: 'create', start: base, end: new Date(base.getTime() + 30 * 60000), allDay: false, color: DEFAULT_EVENT_COLOR });
  };

  const monthLabel = anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  // ---- Month grid ----------------------------------------------------------
  const monthCells = useMemo(() => {
    const gridStart = startOfWeekSun(startOfMonth(anchor));
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }, [anchor]);

  const todayKey = localDayKey(new Date());

  const renderMonth = () => (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <div className="grid grid-cols-7 border-b border-gray-100 dark:border-gray-700">
        {WEEKDAYS.map((w) => (
          <div key={w} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
            <span className="hidden sm:inline">{w}</span>
            <span className="sm:hidden">{w[0]}</span>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {monthCells.map((cell) => {
          const key = localDayKey(cell);
          const dayItems = itemsByDay.get(key) || [];
          const dayAbsences = absencesByDay.get(key) || [];
          const otherMonth = cell.getMonth() !== anchor.getMonth();
          const isToday = key === todayKey;
          const shown = dayItems.slice(0, 3);
          const overflow = dayItems.length - shown.length;
          const shownAbsences = dayAbsences.slice(0, 2);
          const absenceOverflow = dayAbsences.length - shownAbsences.length;
          // Phone cells are buttons in all but name: a tap opens the day's sheet.
          const phoneLabel = isPhone
            ? `${cell.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}: ${
                dayItems.length === 0 ? 'nothing scheduled' : `${dayItems.length} ${dayItems.length === 1 ? 'item' : 'items'}`
              }${dayAbsences.length > 0 ? `, ${dayAbsences.length} out` : ''}`
            : undefined;
          return (
            <div
              key={key}
              // Phone: open the day (its list, plus "New event this day" and
              // "Mark someone out this day"). Desktop: click = new event at 9am.
              onClick={() => (isPhone ? setDayDetail(cell) : openCreate(cell))}
              role={isPhone ? 'button' : undefined}
              tabIndex={isPhone ? 0 : undefined}
              aria-label={phoneLabel}
              onKeyDown={isPhone ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setDayDetail(cell);
                }
              } : undefined}
              className={`min-h-[92px] border-b border-r border-gray-100 p-1.5 dark:border-gray-700 max-sm:min-h-[3.5rem] max-sm:cursor-pointer max-sm:p-1 sm:min-h-[96px] 2xl:min-h-[116px] ${
                canCreate ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40' : ''
              } ${otherMonth ? 'bg-gray-50/60 dark:bg-gray-900/30' : ''}`}
            >
              <div className="mb-1 flex items-center justify-between">
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                    isToday
                      ? 'bg-primary-600 text-white'
                      : otherMonth
                      ? 'text-gray-400 dark:text-gray-600'
                      : 'text-gray-700 dark:text-gray-200'
                  }`}
                >
                  {cell.getDate()}
                </span>
                {/* Phone: someone is out that day (the sheet names them). */}
                {isPhone && dayAbsences.length > 0 && (
                  <UserMinus className="h-3 w-3 flex-shrink-0 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                )}
              </div>
              {isPhone ? (
                // Phone: one dot per item (the first three, in the same colors
                // the chips use — type, or person when several calendars are
                // shown; a finished item is faded) and a "+N" for the rest.
                dayItems.length > 0 && (
                  <div className="flex items-center gap-0.5">
                    {dayItems.slice(0, 3).map((it) => (
                      <span
                        key={keyOf(it)}
                        className={`h-1.5 w-1.5 flex-shrink-0 rounded-full ${leadingDot(it)} ${struck(it) ? 'opacity-40' : ''}`}
                      />
                    ))}
                    {dayItems.length > 3 && (
                      <span className="ml-0.5 text-[10px] font-medium leading-none tabular-nums text-gray-500 dark:text-gray-400">
                        +{dayItems.length - 3}
                      </span>
                    )}
                  </div>
                )
              ) : (
              <div className="space-y-1">
                {/* Out-of-office bands sit above the day's events and are drawn
                    unlike them on purpose — they belong to the team, not to a
                    person's private calendar. */}
                {shownAbsences.map((a) => absenceBand(a))}
                {absenceOverflow > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDayDetail(cell);
                    }}
                    className="block w-full text-left text-[11px] font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400"
                  >
                    +{absenceOverflow} out
                  </button>
                )}
                {shown.map((it) => {
                  const terminal = struck(it);
                  return (
                    <button
                      key={keyOf(it)}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleItemClick(it);
                      }}
                      title={`${multiPerson ? `${ownerLabel(it.owner_user_id)} · ` : ''}${it.title}${it.outcome ? ` · ${it.outcome.label}` : ''}`}
                      className={`flex w-full items-center truncate text-left ${chipBase} ${chipCls(it)} ${terminal ? 'line-through opacity-60' : ''}`}
                    >
                      {multiPerson && <span className={`mr-1 h-1.5 w-1.5 flex-shrink-0 rounded-full ${personColor(it.owner_user_id).dot}`} />}
                      {/* A past callback's outcome, as a ringed dot (the pill is in the Agenda). */}
                      {it.outcome && (
                        <span
                          aria-label={it.outcome.label}
                          className={`mr-1 h-2 w-2 flex-shrink-0 rounded-full ring-1 ring-white dark:ring-gray-800 ${outcomeDotCls(it.outcome)}`}
                        />
                      )}
                      {!it.all_day && <span className="mr-1 hidden tabular-nums opacity-70 sm:inline">{clockTime(it.start)}</span>}
                      <span className="truncate">{it.title}</span>
                    </button>
                  );
                })}
                {overflow > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDayDetail(cell);
                    }}
                    className="block w-full text-left text-[11px] font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400"
                  >
                    +{overflow} more
                  </button>
                )}
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // ---- Agenda --------------------------------------------------------------
  // A day with nothing but an absence on it still earns a row — "Dana is out
  // Thursday" is exactly the thing the agenda is being read for.
  // ONE day at a time (owner ask 2026-10-09): the arrows step through days, so
  // yesterday's calls and their outcomes are one press away.
  const agendaKey = localDayKey(agendaDay);
  const agendaDays = useMemo(() => {
    const items = itemsByDay.get(agendaKey) || [];
    const abs = absencesByDay.get(agendaKey) || [];
    return items.length || abs.length ? [{ key: agendaKey, items, absences: abs }] : [];
  }, [itemsByDay, absencesByDay, agendaKey]);

  const renderAgenda = () => {
    if (agendaDays.length === 0) {
      return (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center dark:border-gray-700 dark:bg-gray-800">
          <CalendarDays className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            Nothing on {agendaDay.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}.
          </p>
          {canCreate && (
            <button onClick={() => openCreate(agendaDay)} className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700">
              <Plus className="h-4 w-4" /> New event this day
            </button>
          )}
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {agendaDays.map(({ key, items: dayItems, absences: dayAbsences }) => {
          const tally = outcomeTally(dayItems);
          const pastCallbacks = dayItems.filter((it) => isPastCallback(it)).length;
          return (
          <div key={key} className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-100 px-4 py-2 dark:border-gray-700">
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{dayLabel(`${key}T12:00:00`)}</span>
              {/* How the day's calls went, once any slot has passed. */}
              {pastCallbacks > 0 && (
                <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-gray-500 dark:text-gray-400" data-outcome-tally>
                  <span>{pastCallbacks} {pastCallbacks === 1 ? 'call' : 'calls'}</span>
                  {tally.map((t) => (
                    <span key={t.label} className="inline-flex items-center gap-1 whitespace-nowrap">
                      <span className={`h-1.5 w-1.5 rounded-full ${toneDot(t.tone)}`} />
                      <span className="tabular-nums">{t.n}</span> {t.label}
                    </span>
                  ))}
                </span>
              )}
            </div>
            {dayAbsences.length > 0 && (
              <div className="divide-y divide-gray-100 border-b border-gray-100 dark:divide-gray-700 dark:border-gray-700">
                {dayAbsences.map((a) => (
                  <button
                    key={`abs-${a.id}`}
                    type="button"
                    onClick={() => setAbsenceModal({ mode: 'view', absence: a })}
                    title={absenceTitle(a)}
                    className="flex min-h-[44px] w-full items-center gap-3 border-l-2 border-dashed border-gray-400 bg-gray-50 px-4 py-2 text-left transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-500 dark:bg-gray-900/40 dark:hover:bg-gray-700/40 sm:min-h-0"
                  >
                    <span className="w-14 flex-shrink-0 text-xs text-gray-500 dark:text-gray-400 sm:w-20">Out</span>
                    <UserMinus className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                    <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-700 dark:text-gray-200">
                      {a.user_name || 'Recruiter'}
                      {a.user_id === user?.id && <span className="ml-1 text-xs font-normal text-gray-400">(you)</span>}
                      <span className="ml-1.5 text-xs font-normal text-gray-500 dark:text-gray-400">
                        {absenceKindLabel(a.kind)}
                        {backOnLabel(a.ends_on) ? ` · back ${backOnLabel(a.ends_on)}` : ''}
                      </span>
                    </span>
                    <span className="flex-shrink-0 rounded border border-dashed border-gray-400 px-1.5 py-0.5 text-xs font-medium text-gray-500 dark:border-gray-500 dark:text-gray-400">
                      Out of office
                    </span>
                  </button>
                ))}
              </div>
            )}
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {dayItems.map((it) => {
                const terminal = struck(it);
                const ec = it.source_kind === 'event' ? eventColor(it.color) : null;
                const border = multiPerson
                  ? personColor(it.owner_user_id).border
                  : ec
                  ? ec.border
                  : it.source_kind === 'callback'
                  ? 'border-cyan-400'
                  : 'border-amber-400';
                const past = isPastCallback(it);
                // The AI's line about the call (or a person's note with their
                // choice) reads first; the booking note stays as the fallback.
                const subline = (past && it.outcome?.summary) || it.location || it.note;
                return (
                  <div
                    key={keyOf(it)}
                    className={`flex w-full items-center gap-3 border-l-2 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 max-sm:flex-wrap max-sm:gap-y-1.5 ${border}`}
                  >
                    {/* Phone: a past callback's row gives the name its full width
                        and drops the outcome menu and chip to a line of their own. */}
                    <button type="button" onClick={() => handleItemClick(it)} className={`flex min-h-[44px] min-w-0 flex-1 items-center gap-3 text-left sm:min-h-0 ${past ? 'max-sm:basis-full' : ''}`}>
                      <span className="w-14 flex-shrink-0 text-xs tabular-nums text-gray-500 dark:text-gray-400 sm:w-20">
                        {it.all_day ? 'All day' : clockTime(it.start)}
                      </span>
                      <span className={`h-2 w-2 flex-shrink-0 rounded-full ${leadingDot(it)}`} />
                      <span className="min-w-0 flex-1">
                        <span className={`block truncate text-sm font-medium text-gray-900 dark:text-gray-100 ${terminal ? 'line-through opacity-60' : ''}`}>
                          {multiPerson && (
                            <span className="font-normal text-gray-500 dark:text-gray-400">{firstName(ownerLabel(it.owner_user_id))} · </span>
                          )}
                          {it.title}
                        </span>
                        {past && (
                          <span className="mt-0.5 flex min-w-0 items-center" data-callback-outcome>
                            <OutcomeBlock outcome={it.outcome} />
                          </span>
                        )}
                        {subline && (
                          <span className="mt-0.5 block truncate text-xs text-gray-500 dark:text-gray-400" title={subline}>{subline}</span>
                        )}
                      </span>
                    </button>
                    {past && (
                      <OutcomeSelect
                        item={it}
                        onSaved={() => {
                          notifyStatsChanged();
                          load(true);
                        }}
                        onError={setTakeError}
                      />
                    )}
                    {canTake(it, user?.id) && (
                      <button
                        type="button"
                        onClick={() => takeCallback(it)}
                        disabled={takingId === it.source_id}
                        title={`Take this callback from ${ownerLabel(it.owner_user_id)}`}
                        className="inline-flex flex-shrink-0 items-center gap-1 rounded border border-primary-300 px-1.5 py-0.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:opacity-50 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-900/30 max-sm:min-h-[44px] max-sm:px-2.5"
                      >
                        {takingId === it.source_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <PhoneIncoming className="h-3 w-3" />}
                        Take
                      </button>
                    )}
                    <span className={`flex-shrink-0 ${chipBase} ${chipCls(it)} ${terminal ? 'opacity-60' : ''}`}>
                      {it.source_kind === 'callback' ? 'Callback' : it.source_kind === 'task' ? 'Task' : 'Event'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          );
        })}
      </div>
    );
  };

  // ---- Day-detail popover (month "+N more") --------------------------------
  const renderDayDetail = () => {
    if (!dayDetail) return null;
    const dayItems = itemsByDay.get(localDayKey(dayDetail)) || [];
    const dayAbsences = absencesByDay.get(localDayKey(dayDetail)) || [];
    return (
      // A centered card from sm up; on a phone it rises from the bottom as a
      // sheet (the surface the whole month taps into), clear of the home bar.
      <div className={`fixed inset-0 ${Z.modal} flex items-center justify-center bg-black/50 p-4 max-sm:items-end max-sm:p-0`} onClick={() => setDayDetail(null)}>
        <div
          className="max-h-[80vh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-4 shadow-xl dark:bg-gray-800 max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-2xl max-sm:pb-[calc(1rem+env(safe-area-inset-bottom))]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {dayDetail.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
            </h3>
            <button
              type="button"
              onClick={() => setDayDetail(null)}
              aria-label="Close"
              className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 max-sm:inline-flex max-sm:h-11 max-sm:w-11 max-sm:items-center max-sm:justify-center max-sm:p-0"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {dayAbsences.length === 0 && dayItems.length === 0 && (
            <p className="px-2 py-3 text-sm text-gray-500 dark:text-gray-400">Nothing scheduled.</p>
          )}
          <div className="space-y-1">
            {dayAbsences.map((a) => (
              <button
                key={`abs-${a.id}`}
                type="button"
                onClick={() => {
                  setDayDetail(null);
                  setAbsenceModal({ mode: 'view', absence: a });
                }}
                title={absenceTitle(a)}
                className="flex min-h-[44px] w-full items-center gap-2 rounded-lg border border-dashed border-gray-300 px-2 py-1.5 text-left transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:hover:bg-gray-700/40 sm:min-h-0"
              >
                <UserMinus className="h-3.5 w-3.5 flex-shrink-0 text-gray-400" />
                <span className="w-16 flex-shrink-0 text-xs text-gray-500">Out</span>
                <span className="min-w-0 flex-1 truncate text-sm text-gray-700 dark:text-gray-200">
                  {a.user_name || 'Recruiter'}
                  {backOnLabel(a.ends_on) && (
                    <span className="ml-1 text-xs text-gray-400">back {backOnLabel(a.ends_on)}</span>
                  )}
                </span>
              </button>
            ))}
            {dayItems.map((it) => {
              const terminal = struck(it);
              return (
                <div key={keyOf(it)} className="flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 sm:min-h-0">
                  <button
                    type="button"
                    onClick={() => {
                      setDayDetail(null);
                      handleItemClick(it);
                    }}
                    className="flex min-h-[44px] min-w-0 flex-1 items-center gap-2 text-left sm:min-h-0"
                  >
                    <span className={`h-2 w-2 flex-shrink-0 rounded-full ${leadingDot(it)}`} />
                    <span className="w-16 flex-shrink-0 text-xs tabular-nums text-gray-500">{it.all_day ? 'All day' : clockTime(it.start)}</span>
                    <span className={`min-w-0 flex-1 truncate text-sm text-gray-900 dark:text-gray-100 ${terminal ? 'line-through opacity-60' : ''}`}>
                      {multiPerson && <span className="text-gray-500 dark:text-gray-400">{firstName(ownerLabel(it.owner_user_id))} · </span>}
                      {it.title}
                    </span>
                    {it.outcome && <OutcomePill outcome={it.outcome} compact />}
                  </button>
                  {canTake(it, user?.id) && (
                    <button
                      type="button"
                      onClick={() => takeCallback(it)}
                      disabled={takingId === it.source_id}
                      title={`Take this callback from ${ownerLabel(it.owner_user_id)}`}
                      className="inline-flex flex-shrink-0 items-center gap-1 rounded border border-primary-300 px-1.5 py-0.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 disabled:opacity-50 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-900/30"
                    >
                      {takingId === it.source_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <PhoneIncoming className="h-3 w-3" />}
                      Take
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              const d = dayDetail;
              setDayDetail(null);
              setAgendaDay(startOfDay(d));
              setViewPref('agenda');
            }}
            className="mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <CalendarDays className="h-4 w-4" /> Open this day in Agenda
          </button>
          {canCreate && (
            <button
              onClick={() => {
                const d = dayDetail;
                setDayDetail(null);
                openCreate(d);
              }}
              className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
            >
              <Plus className="h-4 w-4" /> New event this day
            </button>
          )}
          <button
            onClick={() => {
              const d = dayDetail;
              setDayDetail(null);
              setAbsenceModal({ mode: 'create', day: localDayKey(d) });
            }}
            className="mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            <UserMinus className="h-4 w-4" /> Mark someone out this day
          </button>
        </div>
      </div>
    );
  };

  // ---- Header controls: built once, placed per tier ------------------------
  // Desktop: all five groups in the PageHeader's action slot, as before.
  // Phone: the month arrows + Today share the title row; the other four make a
  // toolbar row under it, the recruiter picker taking the width that is left.
  // Only the arrangement differs — each control is mounted once per tier.
  // Agenda: the same three controls step ONE DAY (and the arrow keys do too).
  const isAgenda = view !== 'month';
  const stepBack = () => (isAgenda ? setAgendaDay((d) => addDays(d, -1)) : setAnchor((a) => addMonths(a, -1)));
  const stepForward = () => (isAgenda ? setAgendaDay((d) => addDays(d, 1)) : setAnchor((a) => addMonths(a, 1)));
  const goToday = () => (isAgenda ? setAgendaDay(startOfDay(new Date())) : setAnchor(new Date()));
  const monthNav = (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={stepBack}
        aria-label={isAgenda ? 'Previous day' : 'Previous month'}
        title={isAgenda ? 'Previous day (←)' : 'Previous month'}
        className="rounded-lg p-2.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 max-sm:inline-flex max-sm:h-11 max-sm:w-11 max-sm:items-center max-sm:justify-center max-sm:p-0 sm:p-1.5"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={goToday}
        className="rounded-lg border border-gray-300 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 max-sm:min-h-[44px] max-sm:px-3"
      >
        Today
      </button>
      <button
        type="button"
        onClick={stepForward}
        aria-label={isAgenda ? 'Next day' : 'Next month'}
        title={isAgenda ? 'Next day (→)' : 'Next month'}
        className="rounded-lg p-2.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 max-sm:inline-flex max-sm:h-11 max-sm:w-11 max-sm:items-center max-sm:justify-center max-sm:p-0 sm:p-1.5"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );

  // Agenda title: the day, with "Today" / "Yesterday" / "Tomorrow" when it is one.
  const agendaTitle = (() => {
    const diff = Math.round((startOfDay(agendaDay).getTime() - startOfDay(new Date()).getTime()) / 86400000);
    const rel = diff === 0 ? 'Today' : diff === -1 ? 'Yesterday' : diff === 1 ? 'Tomorrow' : null;
    const label = agendaDay.toLocaleDateString(undefined, {
      weekday: 'long',
      month: isPhone ? 'short' : 'long',
      day: 'numeric',
      ...(agendaDay.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    });
    return rel ? `${rel} · ${label}` : label;
  })();

  // Team day: the columns, and one chip per teammate to show or hide theirs.
  const orderedTeam = useMemo(() => {
    const me = team.filter((m) => m.id === user?.id);
    const others = team.filter((m) => m.id !== user?.id).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    return [...me, ...others];
  }, [team, user?.id]);
  const teamDayItems = useMemo(() => itemsByDay.get(agendaKey) || [], [itemsByDay, agendaKey]);
  const teamCols = useMemo(
    () => teamColumns(team, assignees, user?.id, teamDayItems),
    [team, assignees, user?.id, teamDayItems]
  );
  const teamChips = view === 'team' && orderedTeam.length > 0 ? (
    <div
      className="flex flex-wrap items-center gap-1.5 max-sm:-mx-4 max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:px-4 max-sm:pb-1"
      role="group"
      aria-label="Recruiters shown"
    >
      {orderedTeam.map((m) => {
        const on = assignees.all || assignees.ids.includes(m.id);
        return (
          <button
            key={m.id}
            type="button"
            onClick={() => updateAssignees(toggleAssignee(assignees, m.id, orderedTeam.map((t) => t.id), user?.id))}
            aria-pressed={on}
            title={on ? `Hide ${m.name}'s column` : `Show ${m.name}'s column`}
            className={`inline-flex min-h-[44px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 sm:py-1 ${
              on
                ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                : 'border-dashed border-gray-300 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${personColor(m.id).dot} ${on ? '' : 'opacity-40'}`} />
            {m.name}
            {m.id === user?.id && <span className="text-gray-400">(you)</span>}
            {on && <Check className="h-3 w-3" />}
          </button>
        );
      })}
      {!assignees.all && (
        <button
          type="button"
          onClick={() => updateAssignees({ all: true, ids: [] })}
          className="inline-flex min-h-[44px] flex-shrink-0 items-center whitespace-nowrap rounded-full px-2 text-xs font-medium text-primary-700 hover:underline focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 sm:min-h-0 sm:py-1"
        >
          Show everyone
        </button>
      )}
    </div>
  ) : null;

  const recruiterPicker = team.length > 0 ? (
    <RecruiterMultiSelect team={team} currentUserId={user?.id} value={assignees} onChange={updateAssignees} fill={isPhone} />
  ) : null;

  const viewSwitch = (
    <SegmentedControl
      label="Calendar view"
      value={view}
      onChange={(v) => setViewPref(v as CalendarView)}
      options={[
        { value: 'month', label: 'Month', icon: <CalendarRange className="h-4 w-4" /> },
        { value: 'agenda', label: 'Agenda', icon: <CalendarDays className="h-4 w-4" /> },
        { value: 'team', label: 'Team day', icon: <Columns className="h-4 w-4" /> },
      ]}
    />
  );

  const refreshButton = <RefreshButton onClick={() => load()} loading={loading} label="Refresh calendar" />;

  // Icon-only on a phone (the label is in aria-label and title); word + icon from sm up.
  const newEventButton = (
    <button
      type="button"
      onClick={() => openCreate()}
      disabled={!canCreate}
      aria-label="New event"
      title={canCreate ? 'New event' : 'Include your own calendar to add events'}
      className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 max-sm:h-11 max-sm:w-11 max-sm:flex-shrink-0 max-sm:justify-center max-sm:px-0"
    >
      <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New event</span>
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        icon={CalendarDays}
        title={view === 'month' ? monthLabel : agendaTitle}
        actions={isPhone ? monthNav : (
          <>
            {monthNav}
            {recruiterPicker}
            {viewSwitch}
            {refreshButton}
            {newEventButton}
          </>
        )}
      />

      {/* Phone toolbar row (see the header controls above). */}
      {isPhone && (
        <div className="flex items-center gap-2">
          {recruiterPicker}
          <div className="flex-shrink-0">{viewSwitch}</div>
          <div className="flex-shrink-0">{refreshButton}</div>
          {newEventButton}
        </div>
      )}

      {/* Who's out — the team-visible availability strip (crm.user_absences,
          NOT personal calendar events). */}
      <WhosOutBar
        absences={absences}
        todayKey={todayKey}
        currentUserId={user?.id}
        loading={absenceLoading}
        error={absenceError}
        onMarkOut={() => setAbsenceModal({ mode: 'create', day: null })}
        onSelect={(a) => setAbsenceModal({ mode: 'view', absence: a })}
      />

      {/* Event-type toggles (the per-type "calendars") + per-person legend.
          Phones: ONE swipeable strip, edge to edge, 44px chips — the wrapped
          version stood seven lines tall above the grid. The two explanatory
          notes are desktop-only; the legend scrolls along at the strip's end. */}
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400 max-sm:-mx-4 max-sm:flex-nowrap max-sm:overflow-x-auto max-sm:px-4 max-sm:pb-1 max-sm:[-webkit-overflow-scrolling:touch]">
        {ALL_KINDS.map((k) => {
          const on = visibleKinds.has(k);
          const meta = KIND_META[k];
          return (
            <button
              key={k}
              type="button"
              onClick={() => toggleKind(k)}
              aria-pressed={on}
              title={on ? `Hide ${meta.label.toLowerCase()}` : `Show ${meta.label.toLowerCase()}`}
              className={`inline-flex min-h-[44px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 font-medium transition sm:min-h-0 ${
                on
                  ? 'border-gray-300 bg-white text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200'
                  : 'border-dashed border-gray-300 text-gray-400 dark:border-gray-600 dark:text-gray-500'
              }`}
            >
              <span className={`h-2 w-2 rounded-full ${meta.dot} ${on ? '' : 'opacity-40'}`} />
              {meta.label}
            </button>
          );
        })}

        {/* Out of office is its own layer, not one of the three event-type
            "calendars" — dashed chip, because it is a team fact. */}
        <button
          type="button"
          onClick={() => {
            const next = !showAbsences;
            setShowAbsences(next);
            setPref(ABSENCE_PREF, next ? '1' : '0');
          }}
          aria-pressed={showAbsences}
          title={showAbsences ? 'Hide out of office' : 'Show out of office'}
          className={`inline-flex min-h-[44px] flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-dashed px-2.5 py-1 font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 ${
            showAbsences
              ? 'border-gray-400 bg-white text-gray-700 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-200'
              : 'border-gray-300 text-gray-400 dark:border-gray-600 dark:text-gray-500'
          }`}
        >
          <UserMinus className={`h-3 w-3 ${showAbsences ? '' : 'opacity-40'}`} />
          Out of office
        </button>

        {multiPerson && shownOwners.length > 0 && (
          <div className="ml-1 flex flex-wrap items-center gap-2 border-l border-gray-200 pl-3 dark:border-gray-700 max-sm:flex-shrink-0 max-sm:flex-nowrap">
            <span className="text-gray-400">Calendars:</span>
            {shownOwners.map((o) => (
              <span key={o.id || 'unassigned'} className="inline-flex items-center gap-1.5 whitespace-nowrap">
                <span className={`h-2 w-2 rounded-full ${personColor(o.id || null).dot}`} />
                {o.label}
              </span>
            ))}
          </div>
        )}

        {includesOthers && !multiPerson && (
          <span className="text-gray-400 max-sm:hidden">· Showing another recruiter's callbacks &amp; tasks (their personal events stay private)</span>
        )}
        {includesOthers && multiPerson && (
          <span className="text-gray-400 max-sm:hidden">· Personal events stay private to each owner</span>
        )}
        {showAbsences && (
          <span className="text-gray-400 max-sm:hidden">· Out of office is shown for the whole team, whoever's calendars you're viewing</span>
        )}
      </div>

      {teamChips}

      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}

      {takeError && (
        <Alert tone="error" icon={false} onDismiss={() => setTakeError(null)}>{takeError}</Alert>
      )}

      {loading && items.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      ) : view === 'month' ? (
        renderMonth()
      ) : view === 'team' ? (
        <TeamDayView
          day={agendaDay}
          columns={teamCols}
          items={teamDayItems}
          absences={absencesByDay.get(agendaKey) || []}
          canCreate={canCreate}
          isPhone={isPhone}
          chipClass={chipCls}
          isStruck={struck}
          onItemClick={handleItemClick}
          onCreateAt={(start) =>
            setModal({ mode: 'create', start, end: new Date(start.getTime() + 30 * 60000), allDay: false, color: DEFAULT_EVENT_COLOR })
          }
          onAbsenceClick={(a) => setAbsenceModal({ mode: 'view', absence: a })}
        />
      ) : (
        renderAgenda()
      )}

      {modal && (
        <EventModal
          eventId={modal.mode === 'edit' ? modal.eventId : null}
          initialTitle={modal.mode === 'edit' ? modal.title : ''}
          initialStart={modal.start}
          initialEnd={modal.end}
          initialAllDay={modal.allDay}
          initialLocation={modal.mode === 'edit' ? modal.location : ''}
          initialDescription={modal.mode === 'edit' ? modal.description : ''}
          initialColor={modal.color || DEFAULT_EVENT_COLOR}
          initialRecurrence={modal.mode === 'edit' ? modal.recurrence : null}
          initialRecurrenceDays={modal.mode === 'edit' ? modal.recurrenceDays : null}
          initialRecurrenceUntil={modal.mode === 'edit' ? modal.recurrenceUntil : null}
          initialBlocksBooking={modal.mode === 'edit' ? modal.blocksBooking : true}
          onClose={() => setModal(null)}
          onSaved={() => load(true)}
        />
      )}

      {absenceModal && (
        <AbsenceModal
          absence={absenceModal.mode === 'view' ? absenceModal.absence : null}
          initialUserId={absenceModal.mode === 'create' ? user?.id : null}
          initialStart={absenceModal.mode === 'create' ? absenceModal.day || undefined : undefined}
          onClose={() => setAbsenceModal(null)}
          onSaved={() => load(true)}
        />
      )}

      {renderDayDetail()}
    </div>
  );
}
